let placesLibraryPromise: Promise<google.maps.PlacesLibrary> | null = null

/**
 * Lazy-loads the Maps JS bootstrap script once and resolves the `places` library
 * (Places API New). Rejects when NEXT_PUBLIC_GOOGLE_MAPS_API_KEY is unset or the
 * script fails; the rejection is not cached, so callers can retry.
 */
export function loadPlacesLibrary(): Promise<google.maps.PlacesLibrary> {
    if (!placesLibraryPromise) {
        placesLibraryPromise = load().catch((err) => {
            placesLibraryPromise = null
            throw err
        })
    }
    return placesLibraryPromise
}

function load(): Promise<google.maps.PlacesLibrary> {
    const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY
    if (!apiKey) return Promise.reject(new Error('NEXT_PUBLIC_GOOGLE_MAPS_API_KEY is not set'))

    // Already loaded (e.g. by another script/bundle) — reuse instead of injecting again.
    if (typeof google !== 'undefined' && google.maps?.places) return Promise.resolve(google.maps.places)

    return new Promise((resolve, reject) => {
        const script = document.createElement('script')
        // `libraries=places` loads the lib with the API, so `google.maps.places` is
        // populated when `onload` fires. `importLibrary` is only defined by the
        // dynamic-bootstrap snippet, not a plain script tag — don't rely on it.
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places`
        script.async = true
        script.onload = () => {
            const places = typeof google !== 'undefined' ? google.maps?.places : undefined
            if (places) resolve(places)
            else reject(new Error('Google Maps JavaScript API loaded without the places library'))
        }
        script.onerror = () => reject(new Error('Failed to load Google Maps JavaScript API'))
        document.head.appendChild(script)
    })
}
